import React, { useState, useEffect } from 'react';
import { Download, Search, Paperclip, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexCursosInscripciones() {
    const [cursos, setCursos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isGlobalOpen, setIsGlobalOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedCurso, setSelectedCurso] = useState(null);
    const [paymentConfig, setPaymentConfig] = useState(null);
    const [fechaLimiteGlobal, setFechaLimiteGlobal] = useState(null);

    // Timezone-safe date formatter: extracts YYYY-MM-DD to avoid UTC shift
    const formatSafeDate = (dateStr) => {
        if (!dateStr) return '-';
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            const d = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            return d.toLocaleDateString();
        }
        return new Date(dateStr).toLocaleDateString();
    };

    const getSafeDate = (dateStr) => {
        if (!dateStr) return null;
        const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) {
            return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
        }
        return new Date(dateStr);
    };

    const isDeadlinePassed = (curso) => {
        if (!curso) return false;
        const deadline = curso.fecha_limite_inscripcion || curso.fecha_inicio;
        if (!deadline) return false;
        const deadlineDate = getSafeDate(deadline);
        if (!deadlineDate) return false;
        deadlineDate.setHours(23, 59, 59, 999);
        return new Date() > deadlineDate;
    };

    const initialFormData = {
        nombres: '', primerApellido: '', segundoApellido: '',
        cedulaIdentidad: '', extension: '', complemento: '',
        fechaNacimiento: '', edad: '', lugarNacimiento: '', lugarNacimientoOtro: '',
        sexo: '',
        direccion: '', telefono: '', correoElectronico: '',
        contactoNombre: '', contactoParentesco: '', contactoTelefono: '',
        numeroCuotas: '1',
        fotoCedula: null, fotoComprobanteCurso: null
    };

    const [formData, setFormData] = useState(initialFormData);

    useEffect(() => { loadCursos(); }, []);

    useEffect(() => {
        if (showModal) {
            fetch(`${API_URL}/auth/payment-config/curso_extension`)
                .then(res => res.json())
                .then(data => setPaymentConfig(data))
                .catch(err => console.error('Error:', err));
        }
    }, [showModal]);

    useEffect(() => {
        if (formData.fechaNacimiento) {
            const birthDate = new Date(formData.fechaNacimiento);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const m = today.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
            setFormData(prev => ({ ...prev, edad: age.toString() }));
        }
    }, [formData.fechaNacimiento]);

    const loadCursos = async () => {
        try {
            setLoading(true);
            // Verificar estado global (ID 4)
            const globalRes = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/4`).catch(() => null);
            if (globalRes && globalRes.ok) {
                const globalData = await globalRes.json();
                setIsGlobalOpen(globalData.habilitado);
                setFechaLimiteGlobal(globalData.fecha_fin);
            }

            const response = await fetch(`${API_URL}/kardex/cursos`);
            const data = await response.json();
            setCursos(data.cursos || []);
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setLoading(false);
        }
    };


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (fieldName, e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, [fieldName]: file }));
        }
    };

    const abrirInscripcion = async (curso) => {
        // Doble verificación dinámica antes de abrir
        const globalRes = await fetch(`${API_URL}/direccion/parametrizaciones/verificar/4`).catch(() => null);
        if (globalRes && globalRes.ok) {
            const data = await globalRes.json();
            if (!data.habilitado) {
                setIsGlobalOpen(false);
                alert('El periodo de inscripciones ha sido cerrado por Dirección.');
                return;
            }
        }

        if (isDeadlinePassed(curso)) {
            alert('El plazo para este curso ha vencido.');
            return;
        }

        setSelectedCurso(curso);
        setFormData(initialFormData);
        setShowModal(true);
    };

    const handleSubmit = async () => {
        if (!selectedCurso) return;

        try {
            const lugarFinal = formData.lugarNacimiento === 'Otro' ? formData.lugarNacimientoOtro : formData.lugarNacimiento;

            const response = await fetch(`${API_URL}/kardex/cursos/${selectedCurso.id_curso}/inscribir`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    nombres: formData.nombres,
                    apellidos: `${formData.primerApellido} ${formData.segundoApellido}`.trim(),
                    cedula: formData.cedulaIdentidad,
                    extension: formData.extension,
                    complemento: formData.complemento,
                    fecha_nacimiento: formData.fechaNacimiento || null,
                    lugar_nacimiento: lugarFinal,
                    sexo: formData.sexo || null,
                    direccion: formData.direccion,
                    telefono: formData.telefono,
                    email: formData.correoElectronico,
                    contacto_nombre: formData.contactoNombre,
                    contacto_parentesco: formData.contactoParentesco,
                    contacto_telefono: formData.contactoTelefono,
                    numero_cuotas: parseInt(formData.numeroCuotas)
                })
            });

            if (response.ok) {
                alert('Inscripción realizada exitosamente');
                setShowModal(false);
                loadCursos();
            } else {
                const data = await response.json();
                alert(data.message || 'Error al inscribir');
            }
        } catch (error) {
            console.error('Error:', error);
            alert('Error al inscribir');
        }
    };

    const descargarCertificado = (curso) => {
        if (curso.certificado_disponible) {
            alert(`Descargando certificado del curso: ${curso.nombre}`);
        } else {
            alert('Certificado no disponible aún');
        }
    };

    // En vista administrativa permitimos múltiples inscripciones, 
    // pero mantenemos la función por si se requiere verificar pertenencia en el futuro
    const estaInscrito = (cursoId) => {
        // En Kardex siempre retornamos false para que el botón de inscripción esté disponible
        return false;
    };

    const filtered = cursos.filter(c =>
        searchTerm === '' ||
        c.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const extensiones = ['LP', 'CB', 'SC', 'OR', 'PT', 'TJ', 'CH', 'BN', 'PD'];
    const departamentos = ['La Paz', 'Cochabamba', 'Santa Cruz', 'Oruro', 'Potosí', 'Tarija', 'Chuquisaca', 'Beni', 'Pando'];

    return (
        <div className="p-4 md:p-6 overflow-hidden">
            {/* Header */}
            <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Talleres &gt; Inscripción</h1>
            </div>

            <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                <div className="flex flex-wrap gap-4 items-end ">
                    <div className="flex-grow flex items-end gap-2">
                        <div className="relative flex-grow">
                            <div className="relative">
                                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                <input
                                    type="text"
                                    placeholder="Buscar curso..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Buscador */}
            {/* <div className="mb-4 flex items-center gap-3">
                <div className="flex-1 max-w-md relative">
                    <input
                        type="text"
                        placeholder="Buscar curso..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-full"
                    />
                    <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                </div>
            </div> */}

            {/* Tabla de Cursos */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto max-w-full" style={{ maxWidth: 'calc(100vw - 280px)' }}>
                    <table className="w-full text-xs whitespace-nowrap">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-2 py-3 text-center font-semibold border">No</th>
                                <th className="px-2 py-3 text-center font-semibold border">Nombre Curso</th>
                                <th className="px-2 py-3 text-center font-semibold border">Módulos</th>
                                <th className="px-2 py-3 text-center font-semibold border">Fecha Inicio</th>
                                <th className="px-2 py-3 text-center font-semibold border">Fecha Fin</th>
                                <th className="px-2 py-3 text-center font-semibold border">Duración</th>
                                <th className="px-2 py-3 text-center font-semibold border">Días</th>
                                <th className="px-2 py-3 text-center font-semibold border">Hora Inicio</th>
                                <th className="px-2 py-3 text-center font-semibold border">Hora Fin</th>
                                <th className="px-2 py-3 text-center font-semibold border">Lugar</th>
                                <th className="px-2 py-3 text-center font-semibold border">Costo Curso</th>
                                <th className="px-2 py-3 text-center font-semibold border">Nro Cuotas</th>
                                <th className="px-2 py-3 text-center font-semibold border">Límite Dirección</th>
                                <th className="px-2 py-3 text-center font-semibold border">Inscritos</th>
                                <th className="px-2 py-3 text-center font-semibold border">Cupos Disp.</th>
                                <th className="px-2 py-3 text-center font-semibold border">Inscripciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="16" className="px-4 py-8 text-center text-gray-500 border">Cargando...</td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="16" className="px-4 py-8 text-center text-gray-500 border">No hay cursos disponibles</td></tr>
                            ) : filtered.map((curso, index) => (
                                <tr key={curso.id_curso} className="hover:bg-gray-50">
                                    <td className="px-2 py-3 text-center border">{index + 1}</td>
                                    <td className="px-2 py-3 text-center border">{curso.nombre || '-'}</td>
                                    <td className="px-2 py-3 text-center border">
                                        {curso.modalidad || (Array.isArray(curso.modulos) ? `${curso.modulos.length} Módulos` : curso.modulos) || '-'}
                                    </td>
                                    <td className="px-2 py-3 text-center border">{curso.fecha_inicio ? new Date(curso.fecha_inicio).toLocaleDateString() : '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.fecha_fin ? new Date(curso.fecha_fin).toLocaleDateString() : '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.duracion || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.dias || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.hora_inicio || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.hora_fin || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.lugar || '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.costo_curso ? `Bs. ${curso.costo_curso}` : '-'}</td>
                                    <td className="px-2 py-3 text-center border">{curso.numero_cuotas || '-'}</td>
                                    <td className="px-2 py-3 text-center border font-medium text-red-600">
                                        {formatSafeDate(curso.fecha_limite_inscripcion || curso.fecha_inicio)}
                                    </td>
                                    <td className="px-2 py-3 text-center border font-medium">
                                        {curso.inscritos || 0}
                                    </td>
                                    <td className="px-2 py-3 text-center border font-bold">
                                        <span className={curso.disponibles <= 0 ? 'text-red-600' : 'text-green-600'}>
                                            {curso.disponibles ?? curso.capacidad}
                                        </span>
                                    </td>
                                    <td className="px-2 py-3 text-center border">
                                        <button
                                            onClick={() => abrirInscripcion(curso)}
                                            disabled={curso.disponibles <= 0 || isDeadlinePassed(curso) || !isGlobalOpen}
                                            className={`px-3 py-1 rounded text-xs font-medium ${curso.disponibles <= 0 || isDeadlinePassed(curso) || !isGlobalOpen ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-900 text-white hover:bg-blue-800'}`}
                                        >
                                            {curso.disponibles <= 0 ? 'Lleno' : isDeadlinePassed(curso) ? 'Cerrado' : !isGlobalOpen ? 'Cerrado Dirección' : 'Inscribir Alumno'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal de Inscripción */}
            {showModal && selectedCurso && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-900">Añadir Nuevo Estudiante</h2>
                            <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Curso seleccionado */}
                            <div className="bg-blue-50 p-4 rounded-lg">
                                <p className="font-medium text-blue-900">Curso: {selectedCurso.nombre}</p>
                                <p className="text-sm text-gray-600">Costo: Bs. {selectedCurso.costo_curso || 0} | Certificado: Bs. {selectedCurso.costo_certificado || 0}</p>
                            </div>

                            {/* 1. Datos Personales */}
                            <div>
                                <h3 className="text-lg font-semibold mb-4">1. Datos Personales</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Nombres*</label>
                                        <input type="text" name="nombres" value={formData.nombres} onChange={handleInputChange}
                                            placeholder="Ingrese nombres" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Primer Apellido*</label>
                                        <input type="text" name="primerApellido" value={formData.primerApellido} onChange={handleInputChange}
                                            placeholder="Ingrese primer apellido" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Segundo Apellido</label>
                                        <input type="text" name="segundoApellido" value={formData.segundoApellido} onChange={handleInputChange}
                                            placeholder="Ingrese segundo apellido" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium mb-1">Cédula de Identidad*</label>
                                        <input type="text" name="cedulaIdentidad" value={formData.cedulaIdentidad} onChange={handleInputChange}
                                            placeholder="Ej: 12345678" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Extensión</label>
                                        <select name="extension" value={formData.extension} onChange={handleInputChange}
                                            className="w-full px-3 py-2 border rounded-md">
                                            <option value="">Seleccionar</option>
                                            {extensiones.map(e => <option key={e} value={e}>{e}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Complemento</label>
                                        <input type="text" name="complemento" value={formData.complemento} onChange={handleInputChange}
                                            placeholder="Ej: 1A" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Fecha Nacimiento*</label>
                                        <input type="date" name="fechaNacimiento" value={formData.fechaNacimiento} onChange={handleInputChange}
                                            className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Edad*</label>
                                        <input type="text" name="edad" value={formData.edad} readOnly
                                            className="w-full px-3 py-2 border rounded-md bg-gray-100" placeholder="Calculado" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Lugar Nacimiento*</label>
                                        <select name="lugarNacimiento" value={formData.lugarNacimiento} onChange={handleInputChange}
                                            className="w-full px-3 py-2 border rounded-md">
                                            <option value="">Seleccionar</option>
                                            {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
                                            <option value="Otro">Otro</option>
                                        </select>
                                    </div>
                                    {formData.lugarNacimiento === 'Otro' && (
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Especificar Lugar*</label>
                                            <input type="text" name="lugarNacimientoOtro" value={formData.lugarNacimientoOtro} onChange={handleInputChange}
                                                placeholder="Ingrese lugar" className="w-full px-3 py-2 border rounded-md" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Sexo*</label>
                                        <select name="sexo" value={formData.sexo} onChange={handleInputChange}
                                            className="w-full px-3 py-2 border rounded-md">
                                            <option value="">Seleccionar</option>
                                            <option value="M">Masculino</option>
                                            <option value="F">Femenino</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Dirección*</label>
                                        <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange}
                                            placeholder="Ingrese dirección" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                                        <input type="tel" name="telefono" value={formData.telefono} onChange={handleInputChange}
                                            placeholder="Ej: 71234567" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Correo Electrónico*</label>
                                        <input type="email" name="correoElectronico" value={formData.correoElectronico} onChange={handleInputChange}
                                            placeholder="ejemplo@correo.com" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                </div>

                                {/* Contacto */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Nombres y Apellidos Contacto*</label>
                                        <input type="text" name="contactoNombre" value={formData.contactoNombre} onChange={handleInputChange}
                                            placeholder="Nombre completo" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Parentesco*</label>
                                        <input type="text" name="contactoParentesco" value={formData.contactoParentesco} onChange={handleInputChange}
                                            placeholder="Ej: Padre, Madre, Hermano" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                                        <input type="tel" name="contactoTelefono" value={formData.contactoTelefono} onChange={handleInputChange}
                                            placeholder="Ej: 71234567" className="w-full px-3 py-2 border rounded-md" />
                                    </div>
                                </div>
                            </div>

                            {/* 2. Pago de Curso de Extensión */}
                            <div>
                                <h3 className="text-lg font-semibold mb-4">2. Pago de Curso de Extensión</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Número de Cuotas</label>
                                        <select name="numeroCuotas" value={formData.numeroCuotas} onChange={handleInputChange}
                                            className="w-full px-3 py-2 border rounded-md">
                                            <option value="1">1</option>
                                            <option value="2">2</option>
                                            <option value="3">3</option>
                                        </select>
                                    </div>
                                </div>

                                <p className="text-sm text-gray-600 mb-4">Puede efectuar su pago con cualquiera de los dos siguientes métodos de pago.</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="text-center">
                                        <h4 className="font-medium mb-2">Qr</h4>
                                        <div className="w-32 h-32 border-2 rounded-lg mx-auto flex items-center justify-center overflow-hidden">
                                            {paymentConfig?.qr_imagen_url ? (
                                                <img src={paymentConfig.qr_imagen_url} alt="QR" className="w-full h-full object-contain" />
                                            ) : (
                                                <span className="text-gray-400 text-xs">QR no disponible</span>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="font-medium mb-2">Transferencia Bancaria</h4>
                                        <div className="space-y-1 text-sm">
                                            <p><strong>Banco:</strong> {paymentConfig?.banco_nombre || 'Banco Unión S.A.'}</p>
                                            <p><strong>Número de Cuenta:</strong> {paymentConfig?.banco_cuenta || '12348 - 9183 - 829939'}</p>
                                            <p><strong>Tipo de Cuenta:</strong> Ahorros</p>
                                            <p><strong>Nombre Destinatario:</strong> {paymentConfig?.banco_titular || 'EBID'}</p>
                                            <p><strong>Glosa:</strong> Pago inscripción</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 4. Documentación */}
                            <div>
                                <h3 className="text-lg font-semibold mb-4">4. Documentación</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Fotografía Cédula de Identidad*</label>
                                        <div className="relative">
                                            <input type="text" readOnly value={formData.fotoCedula?.name || ''} placeholder="Seleccionar archivo"
                                                className="w-full px-3 py-2 border rounded-md pr-10" />
                                            <label className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
                                                <Paperclip size={18} className="text-gray-400" />
                                                <input type="file" onChange={(e) => handleFileChange('fotoCedula', e)} className="hidden" accept="image/*,.pdf" />
                                            </label>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Fotografía Comprobante Bancario Curso de Extensión*</label>
                                        <div className="relative">
                                            <input type="text" readOnly value={formData.fotoComprobanteCurso?.name || ''} placeholder="Seleccionar archivo"
                                                className="w-full px-3 py-2 border rounded-md pr-10" />
                                            <label className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
                                                <Paperclip size={18} className="text-gray-400" />
                                                <input type="file" onChange={(e) => handleFileChange('fotoComprobanteCurso', e)} className="hidden" accept="image/*,.pdf" />
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Botón Guardar */}
                            <div className="p-4 border-t flex justify-end">
                                <button onClick={handleSubmit}
                                    className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800">
                                    Guardar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
